/**
 * The episode's agent and model, and the two writes that change them.
 *
 * Plan: docs/plans/episode-agent-parity.md S8.
 *
 * Deliberately NOT `useModelSelector`, which reads and writes the GLOBAL
 * default engine and default chat model. That hook answers "what does a new
 * conversation start on"; this one answers "what is THIS conversation on", and
 * confusing the two is how a user changes one thing and sees the other move.
 */
import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { episodeKeys } from "../modules/episodes/queries";
import type { AppRuntime } from "../runtime/contracts/runtime";
import type { AgentChoice } from "./EpisodeAgentHeader";

interface ModelRow {
  readonly id: string;
  readonly model_id: string;
}

export interface UseEpisodeAgentResult {
  readonly engines: readonly string[];
  readonly models: readonly AgentChoice[];
  readonly selectEngine: (engine: string) => void;
  readonly selectModel: (model: string | null) => void;
  /** Why the last choice was refused, or null. The backend refuses a frozen
   *  engine and an unrunnable model by design; swallowing that leaves the user
   *  clicking a control that appears to do nothing. */
  readonly error: string | null;
}

export function useEpisodeAgent(
  runtime: AppRuntime,
  episodeId: string | null,
  engine: string | null,
): UseEpisodeAgentResult {
  const queryClient = useQueryClient();

  // `agent.engines` is the sidecar's list filtered by policy — the same list
  // the settings screen offers, because which engines exist is not a
  // per-episode question.
  // Read through the runtime this hook was HANDED, not the ambient one.
  // `useRpcQuery` reaches for the provider's runtime, which would be a second
  // source for the same thing — and would make the hook unmountable wherever
  // the caller has a runtime but no provider.
  const { data: engineData } = useQuery<{ engines?: readonly string[] }>({
    queryKey: ["agent.engines"],
    queryFn: () => runtime.transport.rpc<{ engines?: readonly string[] }>("agent.engines", {}),
  });

  // Filtered by the engine: a CLI engine authenticates against one vendor's
  // subscription, so offering the others would offer a turn that gets refused.
  const { data: modelData } = useQuery<{ models?: readonly ModelRow[] }>({
    queryKey: ["agent.models.list", engine ?? ""],
    queryFn: () =>
      runtime.transport.rpc<{ models?: readonly ModelRow[] }>("agent.models.list", {
        engine,
      }),
    enabled: engine !== null,
  });

  const [error, setError] = useState<string | null>(null);

  const invalidate = useCallback((): void => {
    // `episodeKeys.detail` — the key the detail query is actually registered
    // under. `["episodes", id]` looks like a prefix and is not one: the real
    // key is `["episodes","detail",id]`, so the match failed and the header
    // kept its stale value for the whole 30s staleTime.
    if (episodeId !== null) {
      void queryClient.invalidateQueries({ queryKey: episodeKeys.detail(episodeId) });
    }
    void queryClient.invalidateQueries({ queryKey: ["agent.models.list"] });
  }, [queryClient, episodeId]);

  const failed = useCallback((e: unknown): void => {
    setError(e instanceof Error ? e.message : String(e));
  }, []);

  const selectEngine = useCallback(
    (next: string): void => {
      if (episodeId === null) return;
      setError(null);
      void runtime.transport
        .rpc("episodes.engine.set", { episode_id: episodeId, engine: next })
        .then(invalidate)
        .catch(failed);
    },
    [runtime, episodeId, invalidate, failed],
  );

  const selectModel = useCallback(
    (next: string | null): void => {
      if (episodeId === null) return;
      // An absent `model` and an explicit null mean the same thing to the
      // backend: return the episode to the engine's own default.
      setError(null);
      void runtime.transport
        .rpc(
          "episodes.model.set",
          next === null
            ? { episode_id: episodeId }
            : { episode_id: episodeId, model: next },
        )
        .then(invalidate)
        .catch(failed);
    },
    [runtime, episodeId, invalidate, failed],
  );

  return {
    engines: engineData?.engines ?? [],
    models: (modelData?.models ?? []).map((m) => ({ id: m.id, label: m.model_id })),
    selectEngine,
    selectModel,
    error,
  };
}
